import { Button, ButtonType } from '@ardoq/button';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors, space } from '@ardoq/design-tokens';
import { Box, FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { TextArea } from '@ardoq/forms';
import { TextAreaWithSuggestions } from '@ardoq/select';
import { Tag, StatusType } from '@ardoq/status-ui';
import { useId, type KeyboardEvent } from 'react';
import { t, type I18nLocale, DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import styled from 'styled-components';
import { AiFrame } from './AiFrame';
import { AiLoadingText, type AiLoadingConfig } from './Loaders/AiLoadingText';
import type { AiPopoverInputTextareaProps } from './AIPopover/AiPopoverInput';

type InputProps = {
  value: string;
  placeholder?: string;
  onValueChange: (value: string) => void;
  'aria-label'?: string;
};

type SuggestionsConfig = Pick<
  AiPopoverInputTextareaProps,
  'suggestions' | 'suggestionsIsOpen' | 'onSelectSuggestion' | 'onClickOutside'
>;

type GenerateButtonProps = {
  label: string;
  onClick: VoidFunction;
  isDisabled?: boolean;
};

type ClearButtonProps = {
  label: string;
  onClick: VoidFunction;
};

export type AiInlineInputProps = {
  i18nLocale?: I18nLocale;
  input: InputProps;
  generateButton?: GenerateButtonProps;
  suggestions?: SuggestionsConfig;
  clearButton?: ClearButtonProps;
  loading?: AiLoadingConfig;
  errorMessage?: string | null;
  warningMessage?: string | null;
  isDisabled?: boolean;
  disabledTooltip?: string | null;
};

const getStatusIcon = ({
  hasError,
  hasWarning,
}: {
  hasError: boolean;
  hasWarning: boolean;
}) => {
  if (hasError) return { iconName: IconName.ERROR, color: colors.iconError };
  if (hasWarning)
    return { iconName: IconName.WARNING, color: colors.iconWarning };
  return null;
};

export const AiInlineInput = ({
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  input,
  generateButton,
  suggestions,
  clearButton,
  loading,
  errorMessage,
  warningMessage,
  isDisabled,
  disabledTooltip,
}: AiInlineInputProps) => {
  const hintId = useId();
  const resolvedPlaceholder =
    input.placeholder ??
    t(i18nLocale, 'ai:aiInlineInput.placeholder', 'Describe what you need');
  const resolvedInputAriaLabel =
    input['aria-label'] ??
    t(i18nLocale, 'ai:aiInlineInput.inputAriaLabel', 'Describe what you need');
  const resolvedLoadingLabel =
    loading?.label ??
    t(i18nLocale, 'ai:aiInlineInput.loadingText', 'Thinking...');

  const isLoading = Boolean(loading?.isLoading);
  const isInputDisabled = Boolean(isDisabled);
  const showClearButton =
    Boolean(clearButton) && input.value !== '' && !isLoading && !isDisabled;

  const hasError = Boolean(errorMessage);
  const hasWarning = !hasError && Boolean(warningMessage);
  const statusIcon = getStatusIcon({ hasError, hasWarning });
  const hintMessage = errorMessage ?? warningMessage ?? null;

  const canGenerate =
    Boolean(generateButton) &&
    !generateButton?.isDisabled &&
    !isInputDisabled &&
    !isLoading;

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || !generateButton) return;
    if (canGenerate) {
      event.preventDefault();
      generateButton.onClick();
    }
  };

  if (isLoading) {
    return (
      <FlexBox flexDirection="column" gap="xsmall" width="full">
        <AiLoadingText
          label={resolvedLoadingLabel}
          onCancel={loading?.cancelButton?.onClick}
          cancelLabel={loading?.cancelButton?.label}
        />
      </FlexBox>
    );
  }

  return (
    <FlexBox flexDirection="column" width="full">
      <Box width="full" data-tooltip-text={disabledTooltip ?? undefined}>
        <AiFrame
          align="center"
          gap="small"
          paddingX="small"
          paddingY="xsmall"
          rounded
          surfaceBackground
          outlineOnFocus
          isDisabled={isDisabled}
          hasError={hasError}
          hasWarning={hasWarning}
        >
          <InputColumn>
            {suggestions ? (
              <InlineTextAreaWithSuggestions
                value={input.value}
                onValueChange={input.onValueChange}
                isDisabled={isInputDisabled}
                aria-label={resolvedInputAriaLabel}
                aria-invalid={hasError}
                aria-describedby={hintId}
                placeholder={resolvedPlaceholder}
                rows={1}
                onKeyDown={handleKeyDown}
                suggestionsIsOpen={suggestions.suggestionsIsOpen}
                onSelectSuggestion={suggestions.onSelectSuggestion}
                onClickOutside={suggestions.onClickOutside}
                suggestions={suggestions.suggestions.map(suggestion => ({
                  value: suggestion.value,
                  label: suggestion.value,
                  rightContent: (
                    <Tag
                      statusType={StatusType.INFO}
                      label={suggestion.tagLabel}
                      leftIcon={{ name: suggestion.tagIconName }}
                    />
                  ),
                }))}
              />
            ) : (
              <InlineTextArea
                value={input.value}
                onValueChange={input.onValueChange}
                isDisabled={isInputDisabled}
                aria-label={resolvedInputAriaLabel}
                aria-invalid={hasError}
                aria-describedby={hintId}
                placeholder={resolvedPlaceholder}
                rows={1}
                onKeyDown={handleKeyDown}
              />
            )}
          </InputColumn>
          <FlexBox align="center" gap="xsmall" flexShrink={0}>
            {statusIcon ? (
              <StatusIcon
                iconName={statusIcon.iconName}
                iconSize={IconSize.SMALL}
                color={statusIcon.color}
              />
            ) : null}
            {showClearButton && clearButton ? (
              <Button
                buttonType={ButtonType.GHOST}
                onClick={clearButton.onClick}
              >
                {clearButton.label}
              </Button>
            ) : null}
            {generateButton ? (
              <GenerateButton
                buttonType={ButtonType.PRIMARY}
                onClick={generateButton.onClick}
                isDisabled={isDisabled || generateButton.isDisabled}
              >
                {generateButton.label}
              </GenerateButton>
            ) : null}
          </FlexBox>
        </AiFrame>
      </Box>
      <FlexBox id={hintId} role={hasError ? 'alert' : 'status'}>
        {hintMessage ? (
          <FlexBox align="center" gap="small" paddingTop="xsmall">
            <Text
              variant="caption"
              color={hasError ? 'textError' : 'textWarning'}
            >
              {hintMessage}
            </Text>
          </FlexBox>
        ) : null}
      </FlexBox>
    </FlexBox>
  );
};

// `transform` establishes a containing block so TextAreaWithSuggestions'
// fixed-position menu anchors to the input instead of the viewport.
// 1px padding aligns the size with the AiLoadingText
const InputColumn = styled.div`
  flex-grow: 1;
  min-width: 0;
  transform: translateZ(0);
  padding-top: 1px;
  padding-bottom: 1px;
`;

const InlineTextArea = styled(TextArea)`
  max-height: 60px;
  padding-left: ${space.xsmall};
`;

const InlineTextAreaWithSuggestions = styled(TextAreaWithSuggestions)`
  max-height: 60px;
  padding-left: ${space.xsmall};
`;

const GenerateButton = styled(Button)`
  padding: 0 ${space.xsmall};
  min-height: 0;
`;

const StatusIcon = styled(Icon)`
  margin-right: ${space.xsmall};
`;
